import { useCallback, useEffect, useMemo, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';

import { SageAuth, SageAuthLoginOptions, SageAuthUser } from './SageAuth';
import { toAuth0Error } from './nativeAuthError';
import { clearWebSdkResidue } from './webSdkResidue';
import {
    LOGIN_SUCCESS_REDIRECT_PATH,
    NATIVE_AUTH_CALLBACK_PATH,
    NATIVE_ULP_PENDING_KEY,
} from 'src/constants/Auth0';
import {
    beginNativeWebLogin,
    NativeAuthSession,
    NativeAuthTenant,
    nativeGetAccessToken,
    nativeGetSession,
    nativeLogin,
    nativeLogout,
    nativeSendOtp,
    nativeVerifyOtp,
    onNativeAuthChanged,
} from 'src/native/nativeAuth';

const SIGNED_OUT: NativeAuthSession = { authenticated: false };

function toUser(session: NativeAuthSession): SageAuthUser | undefined {
    if (!session.authenticated) return undefined;
    return {
        sub: session.userId,
        given_name: session.givenName,
        family_name: session.familyName,
        email: session.email,
    };
}

function rethrowAsAuth0Error(cause: unknown): never {
    throw toAuth0Error(cause);
}

/**
 * The shell owns the session, so this holds no refresh token and never refreshes on a timer.
 * Two owners racing a rotating refresh token is what trips Auth0's reuse detection.
 */
export function useNativeAuth(
    tenant: NativeAuthTenant | undefined,
    isNativeAuthActive: boolean
): SageAuth {
    const [session, setSession] = useState<NativeAuthSession>(SIGNED_OUT);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | undefined>();

    useEffect(() => {
        if (!isNativeAuthActive) {
            // Stays loading while disabled: ownership turns on in a render before this effect runs,
            // and "not loading, not authenticated" there reads as a definitive signed-out state.
            setIsLoading(true);
            return undefined;
        }
        let cancelled = false;
        let failed = false;
        const adopt = (next: NativeAuthSession) => {
            if (cancelled) return;
            failed = false;
            setSession(next);
            setError(undefined);
        };
        const removal = onNativeAuthChanged(adopt);
        const readSession = () =>
            nativeGetSession()
                .then(adopt)
                .catch((cause) => {
                    if (cancelled) return;
                    failed = true;
                    setError(cause as Error);
                })
                .finally(() => {
                    if (!cancelled) setIsLoading(false);
                });
        // getSession can refresh over the network, so a failed read is retried once there is a reason
        // to expect a different answer, rather than leaving the user signed out until a restart.
        const retry = () => {
            if (!failed || document.visibilityState !== 'visible') return;
            failed = false;
            // Loading again lets AuthTokenCheck fetch the bearer token on recovery, as it does on boot
            setIsLoading(true);
            readSession();
        };
        window.addEventListener('online', retry);
        document.addEventListener('visibilitychange', retry);
        readSession();
        return () => {
            cancelled = true;
            window.removeEventListener('online', retry);
            document.removeEventListener('visibilitychange', retry);
            removal.then((handle) => handle.remove());
        };
    }, [isNativeAuthActive]);

    const getAccessToken = useCallback(async () => {
        // A forced renewal recovers when the shell can't serve its cached token
        const next = await nativeGetAccessToken(false).catch(() => nativeGetAccessToken(true));
        setSession(next);
        if (!next.authenticated || !next.accessToken) {
            // Same shape the web SDK throws, so the shared 401 handling keeps working.
            throw new Error('Login required');
        }
        return next.accessToken;
    }, []);

    const getIdTokenExpiry = useCallback(async () => {
        const expiry = session.idTokenExpiresAt;
        // The web app works in seconds, matching the raw JWT claim.
        return expiry === undefined ? undefined : Math.floor(expiry / 1000);
    }, [session.idTokenExpiresAt]);

    const login = useCallback(async (options?: SageAuthLoginOptions) => {
        const prompt = options?.forceLogin ? 'login' : undefined;
        if (options?.connection) {
            // Left for the next page to read: adopting it here re-runs App's IdP-initiated login.
            const next = await nativeLogin({
                connection: options.connection,
                loginHint: options.loginHint,
                prompt,
            });
            if (!next.authenticated) {
                // A dismissed Custom Tab; the caller's error path returns the user to login.
                throw new Error('SSO login did not complete');
            }
            window.location.assign(LOGIN_SUCCESS_REDIRECT_PATH);
            return;
        }
        const { authorizeUrl } = await beginNativeWebLogin(
            `${window.location.origin}${NATIVE_AUTH_CALLBACK_PATH}`,
            prompt
        );
        sessionStorage.setItem(NATIVE_ULP_PENDING_KEY, 'true');
        window.location.assign(authorizeUrl);
    }, []);

    const sendOtp = useCallback(async (phoneNumber: string) => {
        await nativeSendOtp(phoneNumber).catch(rethrowAsAuth0Error);
    }, []);

    const verifyOtp = useCallback(async (phoneNumber: string, code: string) => {
        setSession(await nativeVerifyOtp(phoneNumber, code).catch(rethrowAsAuth0Error));
        // Lands where the web flow's /auth_confirmed redirect does.
        window.location.assign(LOGIN_SUCCESS_REDIRECT_PATH);
    }, []);

    const logout = useCallback(async ({ returnTo }: { returnTo: string }) => {
        try {
            setSession(await nativeLogout());
        } catch (cause) {
            // The shell clears its own credentials before it opens the browser, so a cancelled
            // or failed browser step still leaves the user signed out here. Carrying on is what
            // stops a dismissed Custom Tab stranding the app in a signed-in-looking state.
            datadogRum.addError(cause, { context: 'nativeLogout' });
            setSession(SIGNED_OUT);
        }
        // Residue from a session that predates the shell owning auth. Nothing reads it, but it
        // must not outlive a logout on a shared device, so it is cleared either way.
        clearWebSdkResidue();
        // Native ends the Auth0 session but does not navigate; the web app still owns routing.
        // The reload is also what clears the Redux bearer token.
        window.location.assign(returnTo);
    }, []);

    return useMemo(
        () => ({
            owner: 'native' as const,
            tenant,
            isLoading: isNativeAuthActive && isLoading,
            isAuthenticated: session.authenticated,
            user: toUser(session),
            error,
            getAccessToken,
            getIdTokenExpiry,
            login,
            sendOtp,
            verifyOtp,
            logout,
        }),
        [
            tenant,
            isNativeAuthActive,
            isLoading,
            session,
            error,
            getAccessToken,
            getIdTokenExpiry,
            login,
            sendOtp,
            verifyOtp,
            logout,
        ]
    );
}
